import { Link } from 'react-router'

const includedFeatures = [
  'Unlimited daily updates',
  'Photo sharing',
  'Push notifications for parents',
  'Parent invitations',
  'App lock (PIN & biometrics)',
  'iOS, Android & Web access',
  'Data export',
  'Email support',
]

const faqs = [
  {
    question: 'Is there a free trial?',
    answer:
      'Yes! Your first child profile is completely free. You only start paying when you add a second child.',
  },
  {
    question: 'Do parents have to pay?',
    answer:
      'No, parents always use Brightseed for free. Only childminders pay for child profiles.',
  },
  {
    question: 'Can I change my plan later?',
    answer:
      'Absolutely. You can add or remove child profiles at any time. Your billing adjusts automatically.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit and debit cards through our secure payment processor.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees.',
  },
  {
    question: 'What happens to my data if I cancel?',
    answer:
      'You can export all your data before cancelling. After cancellation, your data is retained for 30 days in case you change your mind, then permanently deleted.',
  },
]

export function PricingPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Simple, transparent pricing
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
              No hidden fees, no surprises. Just one simple price per child profile.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-lg">
            <div className="rounded-3xl bg-white border-2 border-primary-200 shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 px-8 py-10 text-center text-white">
                <h2 className="text-2xl font-bold">For Childminders</h2>
                <div className="mt-4 flex items-baseline justify-center gap-1">
                  <span className="text-6xl font-bold">£2</span>
                  <span className="text-xl">/month</span>
                </div>
                <p className="mt-2 text-primary-100">per child profile</p>
              </div>

              {/* Features */}
              <div className="px-8 py-10">
                <p className="text-center text-gray-600 mb-8">
                  Everything you need to keep parents connected
                </p>
                <ul className="space-y-4">
                  {includedFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 text-primary-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <Link
                    to="/register"
                    className="block w-full rounded-full bg-accent-500 px-8 py-4 text-center text-lg font-semibold text-white shadow-lg hover:bg-accent-600 transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </div>

            {/* Parents free callout */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-green-700">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="font-medium">Parents always free</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example pricing */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900">
              How much will I pay?
            </h2>
            <p className="mt-2 text-gray-600">
              Simple maths: £2 × number of children
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-primary-600">2</div>
              <div className="text-gray-600 mb-2">children</div>
              <div className="text-2xl font-bold text-gray-900">£4/month</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border-2 border-primary-200">
              <div className="text-4xl font-bold text-primary-600">5</div>
              <div className="text-gray-600 mb-2">children</div>
              <div className="text-2xl font-bold text-gray-900">£10/month</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="text-4xl font-bold text-primary-600">10</div>
              <div className="text-gray-600 mb-2">children</div>
              <div className="text-2xl font-bold text-gray-900">£20/month</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Frequently asked questions
            </h2>
            <p className="mt-4 text-gray-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="mx-auto max-w-3xl">
            <dl className="space-y-6">
              {faqs.map((faq) => (
                <div
                  key={faq.question}
                  className="rounded-2xl border border-gray-200 p-6 hover:border-primary-200 transition-colors"
                >
                  <dt className="font-semibold text-gray-900">{faq.question}</dt>
                  <dd className="mt-2 text-gray-600">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to get started?
          </h2>
          <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto">
            Join Brightseed today and start keeping parents connected.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="w-full sm:w-auto rounded-full bg-white px-8 py-4 text-lg font-semibold text-primary-600 shadow-lg hover:bg-gray-50 transition-colors"
            >
              Create Free Account
            </Link>
            <Link
              to="/features"
              className="w-full sm:w-auto rounded-full border-2 border-white px-8 py-4 text-lg font-semibold text-white hover:bg-white/10 transition-colors"
            >
              View Features
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
